// const BASE_URL = "http://127.0.0.1:3000";

const BASE_URL = process.env.VUE_APP_API_BASE_URL;

const getToken = () => localStorage.getItem("token");

const handleResponse = async (response) => {
  if (!response.ok) {
    const errorData = await response.json();
    const error = new Error(errorData.message || errorData.errors?.[0]);
    error.status = response.status;
    throw error
  }
  return response.json();
};

const fetchApiForFiles = async (endpoint, method = 'POST', data = null) => {
  const token = getToken();
  const facilityId = JSON.parse(localStorage.getItem('user'))?.FacilityId;
  const userId = JSON.parse(localStorage.getItem('user'))?.UserId;

  // Prepare FormData for POST requests
  let requestBody = data;
  if (method !== 'GET' && data instanceof FormData) {
    // Append facilityId and userId to FormData
    if (facilityId) data.append('facilityId', facilityId);
    if (userId) data.append('userId', userId);
    requestBody = data;
    // Log FormData contents for debugging
    console.log('FormData entries:');
    for (let [key, value] of data.entries()) {
      console.log(`${key}:`, value);
    }
  }

  const options = {
    method,
    headers: {
      ...(token && { Authorization: `Bearer ${token}` }),
      ...(facilityId && { 'X-Facility-Id': facilityId }),
      ...(method === 'GET' && {
        Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      }),
    },
    ...(requestBody && { body: requestBody }), // Use FormData or null
  };

  try {
    console.log(`Fetching ${`${BASE_URL}${endpoint}`} with method ${method}`, options);
    const response = await fetch(`${BASE_URL}${endpoint}`, options);
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Request failed: ${response.status} ${response.statusText} - ${errorText}`);
    }

    const contentType = response.headers.get('Content-Type') || '';
    if (contentType.includes('application/vnd.openxmlformats-officedocument.spreadsheetml.sheet')) {
      return response; // Return raw Response for blob
    }
    return await response.json(); // Return JSON for upload responses
  } catch (error) {
    console.error(`Error in fetchApiForFiles (${endpoint}):`, error.message);
    throw error;
  }
};

const fetchApi = async (endpoint, method = "GET", data = {}) => {
  const token = getToken();
  const facilityId = JSON.parse(localStorage.getItem("user"))?.FacilityId;
  const userId = JSON.parse(localStorage.getItem("user"))?.UserId;

  const options = {
    method,
    headers: {
      "Content-Type": "application/json",
      "Access-Control-Allow-Origin": "*",
      "Access-Control-Allow-Headers":
          "Origin, X-Requested-With, Content-Type, Accept, Authorization",
      "Access-Control-Allow-Methods": "GET, POST, PUT, DELETE, OPTIONS",
      ...(token && { Authorization: `Bearer ${token}` }),
      ...(facilityId && { "X-Facility-Id": facilityId }),
    },
    ...(method !== "GET" && {
      body: JSON.stringify({
        ...data,
        facilityId: facilityId || null,
        userId: userId || null,
      }),
    }),
  };

  const response = await fetch(`${BASE_URL}${endpoint}`, options);
  return await handleResponse(response);
};

// Helper function for CRUD operations
const create = (resource, data) => fetchApi(resource, "POST", data);
const update = (resource, id, data) =>
  fetchApi(`${resource}/${id}`, "PUT", data);
const get = (resource, id = "") =>
  fetchApi(id ? `${resource}/${id}` : resource);
// const remove = (resource, id) => fetchApi(`${resource}/${id}`, "DELETE");
const getWithParams = (resource, params = {}) => {
  // Build the URL
  let url = resource;

  // If there are parameters, append them to the URL
  if (Object.keys(params).length > 0) {
    const queryParams = new URLSearchParams(params).toString(); // Converts the params object to query string
    url = `${resource}?${queryParams}`;
  }

  // Call fetchApi with the constructed URL
  return fetchApi(url);
};

// API client object
const apiClient = {
  baseUrl: () => {
    // return "http://127.0.0.1:3000";
    return process.env.VUE_APP_API_BASE_URL;
  },
  // Authentication
  login: async (credentials) => {
    try {
      const response = await fetchApi("/login", "POST", credentials);
      localStorage.setItem("token", response.token);
      localStorage.setItem("user", JSON.stringify(response.user));
      return response;
    } catch (error) {
      const wrapped = new Error("Login failed: " + error.message);
      wrapped.status = error.status
      throw wrapped;
    }
  },

  getReceptionDashboardData: async () => {
    return await fetchApi("/patient/dashboard_stats", "GET");
  },

  logout: async () => {
    const loginLogId = localStorage.getItem("login_log_id");
    return await fetchApi("/logout", "POST", { login_log_id: loginLogId || null });
  },

  changePassword: async (payload) => {
    return await fetchApi("/users/change_password", "PATCH", payload);
  },

  // Patient management
  getPatientList: async (params) => {
    const queryParams = new URLSearchParams({
      page: params.page || 1,
      per_page: params.per_page || 10,
    }).toString();

    return await fetchApi(`/patient?${queryParams}`, "GET");
  },

  getPatient: async (patientId) => {
    return await get("/patient/" + patientId);
  },

  searchPatients: async (params) => {
    return await getWithParams("/patients/search", params);
  },

  searchPatientWithDetails: async (params) => {
    return await fetchApi("/patients/info/search", "POST", params);
  },

  async createPatient(data) {
    return await create("/patient", data);
  },

  updatePatient: async (patientId, data) => {
    return await update("/patient", patientId, data);
  },

  // Visit Management
  listVisits: async (params) => {
    const query = new URLSearchParams({
      status_type: params.status_type || 'ongoing',
      page:        params.page || 1,
      per_page:    params.per_page || 25,
    }).toString()
    return await fetchApi(`/visit_management?${query}`, 'GET')
  },

  deleteVisit: async (visitId) => {
    return await fetchApi(`/visit_management/${visitId}`, 'DELETE')
  },

  getLoginLogs: async (params = {}) => {
    const queryParams = {
      page:        params.page || 1,
      per_page:    params.per_page || 25,
    }
    if (params.query)     queryParams.query     = params.query
    if (params.from_date) queryParams.from_date = params.from_date
    if (params.to_date)   queryParams.to_date   = params.to_date
    const query = new URLSearchParams(queryParams).toString()
    return await fetchApi(`/login_logs?${query}`, 'GET')
  },

  listArchivedVisits: async (params) => {
    const query = new URLSearchParams({
      status_type: 'archived',
      page:        params.page || 1,
      per_page:    params.per_page || 25,
    }).toString()
    return await fetchApi(`/visit_management?${query}`, 'GET')
  },

  unarchiveVisit: async (visitId) => {
    return await fetchApi(`/visit_management/${visitId}/unarchive`, 'PATCH')
  },

  archiveVisit: async (visitId) => {
    return await fetchApi(`/visit_management/${visitId}/archive`, 'PATCH')
  },

  getArchiveSnapshot: async (visitId) => {
    return await fetchApi(`/visit_management/${visitId}/archive_snapshot`, 'GET')
  },

  searchPatientVisits: async (params) => {
    return await getWithParams("/visits/search", params);
  },

  // Visit management
  createVisit: async (data) => {
    return await create("VisitService/create", data);
  },

  getVisitList: async () => {
    return await fetchApi("/visit/list", "GET");
  },

  searchVisitByPatient: async (patientId) => {
    return await get(`VisitService/searchByPatient/${patientId}`);
  },

  searchVisitByFacility: async (facilityId) => {
    return await get(`VisitService/searchByFacility/${facilityId}`);
  },

  getVisit: async (visitId) => {
    return await get(`VisitService/${visitId}`);
  },

  updateVisit: async (visitId, data) => {
    return await update("VisitService", visitId, data);
  },

  // deleteVisit: async (visitId) => {
  //   return await remove("VisitService/delete", visitId);
  // },

  fetchTriageVisits: async () => {
    return await fetchApi("/triage/visit/list", "GET");
  },

  getConsultationService: async (params) => {
    const queryParams = new URLSearchParams({
      facility_id: params.facility_id,
      service_type_code: params.service_type_code
    }).toString()
    return await fetchApi(`/facility_service/get_consultation_service?${queryParams}`, 'GET')
  },

  getConsultationServices: async () => {
    return await fetchApi('/facility_service/consultation_services')
  },

  verifyConsultationCoverage: async (params) => {
    const queryParams = new URLSearchParams({
      patient_id: params.patient_id,
      visit_id:   params.visit_id,
    });
    return await fetchApi(`/consultation/verify_consultation_coverage?${queryParams}`, 'GET');
  },

  verifyProcedureCoverage: async (params) => {
    const queryParams = new URLSearchParams({
      patient_id: params.patient_id,
      visit_id:   params.visit_id,
    });
    return await fetchApi(`/consultation/verify_procedure_coverage?${queryParams}`, 'GET');
  },

  // Save clinical findings for a single procedure (per-procedure result record)
  updateProcedureResult: async (payload) => {
    return await fetchApi("/consultation/update_procedure_result", "POST", payload);
  },

  verifyCoverage: async (params) => {
    const queryParams = new URLSearchParams({
      patient_id: params.patient_id,
      visit_id: params.visit_id,
    });

    if (params.bill_items) {
      params.bill_items.forEach((item, index) => {
        queryParams.append(`bill_items[${index}][service_id]`, item.service_id);
        queryParams.append(`bill_items[${index}][bill_item_id]`, item.bill_item_id);
      });
    } else if (params.service_id) {
      queryParams.append('service_id', params.service_id);
    }

    return await fetchApi(`/patient/verify_coverage?${queryParams}`, 'GET');
  },

  verifyTriageCoverage: async (params) => {
    const queryParams = new URLSearchParams({
      patient_id: params.patient_id,
      visit_id: params.visit_id,
      bill_id: params.bill_id,
      service_id: params.service_id,
    });

    return await fetchApi(`/triage/verify_triage_coverage?${queryParams}`, 'GET');
  },

  getTriageDashboardData: async () => {
    return await fetchApi("/triage/dashboard/data", "GET");
  },

  async saveTriage(data) {
    return await create("/triage/create", data);
  },

  /**
   * Get patient medical history
   * @param {*} patientId
   * @returns
   */
  getPatientMedicalHistory: async (patientId) => {
    return await fetchApi("/emr/list/" + patientId);
  },

  getVisitEMRDetail: async (visitId) => {
    return await fetchApi("/emr/view/" + visitId);
  },

  updateTriageVitals: async (id, data) => {
    return await fetchApi(`/emr/triage_vitals/${id}`, "PATCH", data)
  },

  updateTriageAssessment: async (id, data) => {
    return await fetchApi(`/emr/triage_assessment/${id}`, "PATCH", data)
  },

  updateConsultation: async (id, data) => {
    return await fetchApi(`/emr/consultation/${id}`, "PATCH", data)
  },

  updatePhysicalExam: async (id, data) => {
    return await fetchApi(`/emr/physical_exam/${id}`, "PATCH", data)
  },

  upsertLabResult: async (labTestId, data) => {
    return await fetchApi(`/emr/lab_tests/${labTestId}/result`, "POST", data)
  },

  upsertRadiologyResult: async (radiologyTestId, data) => {
    return await fetchApi(`/emr/radiology_tests/${radiologyTestId}/result`, "POST", data)
  },

  /**
   * Search patient triage visits
   * @param {*} params
   * @returns
   */
  searchPatientTriageVisits: async (params) => {
    return await getWithParams("/triage/patient/visits/search", params);
  },

  /**
   * Create facility service
   * @param {*} data
   * @returns
   */
  createFacilityService: async (data) => {
    return await create("/facility_service", data);
  },

  /**
   * Get facility services
   * @returns
   */
  getFacilityServices: async (params = {}) => {
    const queryParams = new URLSearchParams({
      page: params.page || 1,
      per_page: params.per_page || 10,
    }).toString();

    return await fetchApi(`/facility_service?${queryParams}`, "GET");
  },

  getDepartmentServices: async (params) => {
    const queryParams = new URLSearchParams({
      department_id: params.department_id,
      page: params.page || 1,
      per_page: params.per_page || 10
    }).toString();

    return await fetchApi(`/facility_service/services_by_department?${queryParams}`, 'GET')
  },

  getDisciplinesForDepartment: async (departmentId) => {
    return await fetchApi(`/facility_service/disciplines_for_department?department_id=${departmentId}`, 'GET')
  },

  getServicesByDiscipline: async (params) => {
    const queryParams = new URLSearchParams({
      department_id: params.department_id,
      discipline:    params.discipline,
      page:          params.page    || 1,
      per_page:      params.per_page || 10
    }).toString()
    return await fetchApi(`/facility_service/services_by_discipline?${queryParams}`, 'GET')
  },

  // Download services template sheet for guidance
  downloadServicesTemplate: async () => {
    const response = await fetch(`${BASE_URL}/facility_service/template`, {
      method: 'GET',
      headers: {
        'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      }
    });

    return response;
  },

  // Bulk upload services for a department
  uploadDepartmentServices: async (payload) => {
    return await fetchApiForFiles("/facility_service/upload", "POST", payload)
  },

  getFacilityServiceTypes: async () => {
    return await fetchApi("/service_types", "GET");
  },

  updateFacilityService: async (id, data) => {
    return await fetchApi("/facility_service/" + id, "PUT", data);
  },
  /**
   * Delete Facility service
   * @param {*} id
   * @returns
   */
  deleteFacilityService: async (id) => {
    return await fetchApi("/facility_service/" + id, "DELETE");
  },

  updateFacilityLogo: async (facilityId, logoUrl) => {
    return await fetchApi(`/facilities/${facilityId}/update_logo`, "PATCH", { logo_url: logoUrl });
  },

  addPatientDocument: async (patientId, doc) => {
    return await fetchApi(`/patient/${patientId}/add_document`, "POST", {
      document: {
        fileName: doc.fileName,
        fileType: doc.fileType,
        fileUrl:  doc.fileUrl,
      }
    });
  },

  deletePatientDocument: async (documentId) => {
    return await fetchApi(`/patient_documents/${documentId}`, "DELETE");
  },
  createInventoryItem: async (data) => {
    return await fetchApi("/inventory", "POST", data);
  },

  /**
   * Update Inventory item
   */
  updateInventoryItem: async (id, data) => {
    return await fetchApi("/inventory/" + id, "PUT", data);
  },

  /**
   * List Inventory items
   */
  listInventoryItems: async (params = {}) => {
    const queryParams = new URLSearchParams({
      page: params.page || 1,
      per_page: params.per_page || 10,
    }).toString();

    return await fetchApi(`/inventory?${queryParams}`, "GET");
  },

  /**
   * List Inventory items
   */
  fetchInventorycreationData: async () => {
    return await fetchApi("/inventory_data", "GET");
  },

  /**
   * Fetch an Inventory item
   */
  getInventoryItem: async (id) => {
    return await fetchApi("/inventory/" + id, "GET");
  },

  /**
   * Create Inventory stock items
   *
   * @param {*} payload
   * @returns
   */
  createInventoryStockItem: async (payload) => {
    return await fetchApi("/inventory_stock", "POST", payload);
  },

  /**
   * Search inventory items
   * @param {*} params
   * @returns
   */
  searchInventoryItems: async (params) => {
    return await getWithParams("/inventory_items/search", params);
  },

  /**
   * Search facility services
   * @param {*} params
   * @returns
   */
  searchFacilityServices: async (params) => {
    return await getWithParams("/facility_services/search", params);
  },

  /**
   * Search facility services for consultation
   * @param {*} payload
   * @returns
   */
  searchConsultationFacilityServices: async (payload) => {
    return await fetchApi("/facility_service/search_items", "POST", payload);
  },

  /**
   * Get all lab samples
   * @returns
   */
  getLabSamples: async () => {
    return await fetchApi("/samples", "GET");
  },

  updateLabSample: async (payload) => {
    console.log('payload looks', payload);
    return await fetchApi("/laboratory/update_sample", "POST", payload);
  },

  listPublishedLabTests: async (params = {}) => {
    const query = new URLSearchParams(params).toString()
    return await fetchApi(`/laboratory/published?${query}`, 'GET')
  },

  getLaboratoryReports: async (payload = {}) => {
    const params = new URLSearchParams({
      page: payload.page || 1,
      per_page: payload.per_page || 20   // or 10, match what you prefer
    });

    // Handle nested filter[xxx] exactly like your FFS example
    if (payload.filter && typeof payload.filter === 'object') {
      Object.entries(payload.filter).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          params.append(`filter[${key}]`, value);
        }
      });
    }

    const queryString = params.toString();
    return await fetchApi(`/laboratory_reports?${queryString}`, 'GET');
  },

  getLaboratoryStats: async (payload = {}) => {
    const params = new URLSearchParams();

    // Add date filters
    if (payload.from_date) params.append('from_date', payload.from_date);
    if (payload.to_date) params.append('to_date', payload.to_date);

    // ✅ CRITICAL: Add ALL filter parameters
    if (payload.filter && typeof payload.filter === 'object') {
      Object.entries(payload.filter).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          params.append(`filter[${key}]`, value);
        }
      });
    }

    const qs = params.toString();
    const url = qs ? `/laboratory_reports/statistics?${qs}` : '/laboratory_reports/statistics';
    return await fetchApi(url, 'GET');
  },

  getRadiologyReports: async (payload = {}) => {
    const params = new URLSearchParams({
      page: payload.page || 1,
      per_page: payload.per_page || 10
    });

    if (payload.filter && typeof payload.filter === 'object') {
      Object.entries(payload.filter).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          params.append(`filter[${key}]`, value);
        }
      });
    }

    const queryString = params.toString();
    return await fetchApi(`/radiology_reports?${queryString}`, 'GET');
  },

  getRadiologyStats: async (payload = {}) => {
    const params = new URLSearchParams();

    // ✅ CRITICAL: Pass the complete filter object
    if (payload.filter && typeof payload.filter === 'object') {
      Object.entries(payload.filter).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          params.append(`filter[${key}]`, value);
        }
      });
    }

    const qs = params.toString();
    return await fetchApi(`/radiology_reports/statistics${qs ? '?' + qs : ''}`, 'GET');
  },

  getPharmacyReports: async (payload = {}) => {
    const params = new URLSearchParams({
      page: payload.page || 1,
      per_page: payload.per_page || 10
    });
    if (payload.filter) {
      Object.entries(payload.filter).forEach(([k, v]) => params.append(`filter[${k}]`, v));
    }
    return await fetchApi(`/pharmacy_reports?${params.toString()}`, 'GET');
  },

  getPharmacyStats: async (payload = {}) => {
    const params = new URLSearchParams();
    if (payload.from_date) params.append('from_date', payload.from_date);
    if (payload.to_date) params.append('to_date', payload.to_date);
    if (payload.filter) {
      Object.entries(payload.filter).forEach(([k, v]) => params.append(`filter[${k}]`, v));
    }
    const qs = params.toString();
    return await fetchApi(`/pharmacy_reports/statistics${qs ? '?' + qs : ''}`, 'GET');
  },

  getOtcBills: async (payload = {}) => {
    const params = new URLSearchParams({
      page: payload.page || 1,
      per_page: payload.per_page || 10
    });

    // Handle nested filter[xxx] exactly like laboratory reports
    if (payload.filter && typeof payload.filter === 'object') {
      Object.entries(payload.filter).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          params.append(`filter[${key}]`, value);
        }
      });
    }

    const queryString = params.toString();
    return await fetchApi(`/otc_sales?${queryString}`, 'GET');
  },

  getOtcStats: async (payload = {}) => {
    const params = new URLSearchParams();

    // Add ALL filter parameters to stats request
    if (payload.filter && typeof payload.filter === 'object') {
      Object.entries(payload.filter).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          params.append(`filter[${key}]`, value);
        }
      });
    }

    const qs = params.toString();
    const url = qs ? `/otc_sales/statistics?${qs}` : '/otc_sales/statistics';
    return await fetchApi(url, 'GET');
  },

  createOtcSale: async (payload) => {
    return await fetchApi("/otc_sales", "POST", payload);
  },

  getTriageReports: async (payload = {}) => {
    const params = new URLSearchParams({
      page: payload.page || 1,
      per_page: payload.per_page || 10
    });

    if (payload.filter && typeof payload.filter === 'object') {
      Object.entries(payload.filter).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          params.append(`filter[${key}]`, value);
        }
      });
    }

    return await fetchApi(`/triage_reports?${params.toString()}`, 'GET');
  },

  getTriageStats: async (payload = {}) => {
    const params = new URLSearchParams();

    if (payload.filter && typeof payload.filter === 'object') {
      Object.entries(payload.filter).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          params.append(`filter[${key}]`, value);
        }
      });
    }

    const qs = params.toString();
    return await fetchApi(`/triage_reports/statistics${qs ? '?' + qs : ''}`, 'GET');
  },

  getConsultationReports: async (payload = {}) => {
    const params = new URLSearchParams({
      page: payload.page || 1,
      per_page: payload.per_page || 10
    });

    if (payload.filter && typeof payload.filter === 'object') {
      Object.entries(payload.filter).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          params.append(`filter[${key}]`, value);
        }
      });
    }

    return await fetchApi(`/consultation_reports?${params.toString()}`, 'GET');
  },

  getConsultationStats: async (payload = {}) => {
    const params = new URLSearchParams();

    // ✅ CRITICAL: Pass the complete filter object
    if (payload.filter && typeof payload.filter === 'object') {
      Object.entries(payload.filter).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          params.append(`filter[${key}]`, value);
        }
      });
    }

    const qs = params.toString();
    return await fetchApi(`/consultation_reports/statistics${qs ? '?' + qs : ''}`, 'GET');
  },

  getBillReports: async (payload = {}) => {
    const params = new URLSearchParams({ page: payload.page || 1, per_page: payload.per_page || 10 })
    if (payload.filter) Object.entries(payload.filter).forEach(([k,v]) => params.append(`filter[${k}]`, v))
    return await fetchApi(`/bill_reports?${params.toString()}`, 'GET')
  },

  getBillStats: async (payload = {}) => {
    const params = new URLSearchParams()
    if (payload.filter) Object.entries(payload.filter).forEach(([k,v]) => params.append(`filter[${k}]`, v))
    const qs = params.toString()
    return await fetchApi(`/bill_reports/statistics${qs ? '?' + qs : ''}`, 'GET')
  },

  getMoh706Report: async (params) => {
    const queryParams = new URLSearchParams(params).toString()
    return await fetchApi(`/laboratory_reports/moh_706?${queryParams}`, 'GET')
  },

  getMoh705aReport: async (params) => {
    const queryParams = new URLSearchParams(params).toString()
    return await fetchApi(`/consultation_reports/moh_705a?${queryParams}`, 'GET')
  },

  getMoh705bReport: async (params) => {
    const queryParams = new URLSearchParams(params).toString()
    return await fetchApi(`/consultation_reports/moh_705b?${queryParams}`, 'GET')
  },

  /**
   * Get Consultation visits
   * @returns
   */
  getConsultationVisits: async (params) => {
    const queryParams = new URLSearchParams({
      page: params.page || 1,
      per_page: params.per_page || 10,
    }).toString();

    return await fetchApi(`/consultation?${queryParams}`, "GET");
  },

  // Unified queue: returning patients (with consultation) sorted above new patients
  getUnifiedConsultationQueue: async (params) => {
    const queryParams = new URLSearchParams({
      page: params?.page || 1,
      per_page: params?.per_page || 25,
    }).toString();
    return await fetchApi(`/consultation/unified_queue?${queryParams}`, "GET");
  },

  // Fetch ongoing consultations (Continue Consultation)
  getConsultationResultsQueue: async (params) => {
    const queryParams = new URLSearchParams({
      page: params.page || 1,
      per_page: params.per_page || 10,
    }).toString();

    return await fetchApi(`/consultation/results_queue?${queryParams}`, "GET");
  },

  // NEW: Fetch procedures queue (Start Procedures)
  getProceduresQueue: async (params) => {
    const queryParams = new URLSearchParams({
      page: params.page || 1,
      per_page: params.per_page || 25,
    }).toString();

    return await fetchApi(`/consultation/procedures_queue?${queryParams}`, "GET");
  },

  // NEW: Fetch procedures results queue (Continue Procedures)
  getProceduresResultsQueue: async (params) => {
    const queryParams = new URLSearchParams({
      page: params.page || 1,
      per_page: params.per_page || 25,
    }).toString();

    return await fetchApi(`/consultation/procedures_results_queue?${queryParams}`, "GET");
  },

  /**
   * Get consultation visit details
   * @param {*} visitId
   * @returns
   */
  getConsultationVisitDetails: async (visitId) => {
    return await fetchApi("/consultation/" + visitId, "GET");
  },

  /**
   * Search for diagnosis
   * @param {*} payload
   * @returns
   */
  searchDiagnosis: async (payload) => {
    return await fetchApi("/diagnosis/search", "POST", payload);
  },

  /**
   * Get drugs administration routes
   * @returns
   */
  getAdminstrationRoutes: async () => {
    return await fetchApi("/administration_route", "GET");
  },

  /**
   * Get dosage frequencies
   * @returns
   */
  getDosageFrequencies: async () => {
    return await fetchApi("/inventory_dosage_frequency", "GET");
  },

  /**
   * Search consultation inventory items
   * @param {*} params
   * @returns
   */
  searchConsultationInventoryItems: async (payload) => {
    return await fetchApi(
      "/inventory_items/consultation/search",
      "POST",
      payload
    );
  },

  checkDrugAllergy: async (params) => {
    const queryParams = new URLSearchParams({
      patient_id: params.patient_id,
      inventory_item_id: params.inventory_item_id
    }).toString();

    return await fetchApi(`/consultation/check_drug_allergy?${queryParams}`, 'GET')
  },

  checkInventoryItemAvailability: async (payload) => {
    return await fetchApi("/inventory_items/check_availability", "POST", payload);
  },

  savePatientConsultation: async (payload) => {
    return await fetchApi("/consultation", "POST", payload);
  },

  /**
   * Get consultation data
   * @returns
   */
  getConsultationDashboardData: async () => {
    return await fetchApi("/consultation_dashboard_data", "GET");
  },

  /**
   * Search consultation data
   * @returns
   */
  searchConsultation: async (payload) => {
    return await fetchApi("/search/consultation", "POST", payload);
  },

  /**
   *
   * @returns
   */
  listPatientLabTests: async () => {
    return await fetchApi("/laboratory", "GET");
  },

  getPatientVisitLabTests: async (visitId) => {
    return await fetchApi("/laboratory/" + visitId, "GET");
  },

  getPendingBillItems: async (visitId) => {
    const queryParams = new URLSearchParams({
      visit_id: visitId
    }).toString();

    return await fetchApi(`/billing/pending_bill_items?${queryParams}`, 'GET')
  },

  fetchArchivedBills: async (params) => {
    const queryParams = new URLSearchParams({
      page:     params?.page || 1,
      per_page: params?.per_page || 10,
    }).toString()
    return await fetchApi(`/billing/archived/bills?${queryParams}`, "GET")
  },

  /**
   * Lab reject lab test
   * @param {*} payload
   * @returns
   */
  rejectLabTest: async (payload) => {
    return await fetchApi("/reject/lab/test", "POST", payload);
  },

  verifyLaboratoryCoverage: async (params) => {
    const queryParams = new URLSearchParams({
      patient_id: params.patient_id,
      visit_id: params.visit_id,
      bill_id: params.bill_id,
    }).toString();

    return await fetchApi(`/laboratory/verify_laboratory_coverage?${queryParams}`, "GET");
  },

  /**
   * Collect sample
   * @returns
   */
  collectLabSample: async (payload) => {
    return await fetchApi("/collect/lab/sample", "POST", payload);
  },

  /**
   * Capture lab sample result
   * @param {*} payload
   * @returns
   */
  captureSampleResult: async (payload) => {
    return await fetchApi("/capture/lab/results", "POST", payload);
  },

  /**
   * Get lab sample results
   * @returns
   */
  reviewLabResults: async (payload) => {
    return await fetchApi("/review/lab/results", "POST", payload);
  },

  addExternalLabTest: async (payload) => {
    return await fetchApi("/laboratory/external_test", "POST", payload);
  },

  fetchAnalytesForService: async (facilityServiceId) => {
    return await fetchApi(`/laboratory/analytes_for_services?facility_service_id=${facilityServiceId}`, "GET");
  },

  updateServiceAnalytes: async (facilityServiceId, analyteIds) => {
    return await fetchApi('/laboratory/update_service_analytes', 'POST', {
      facility_service_id: facilityServiceId,
      analyte_ids: analyteIds
    })
  },

  fetchBillingStatus: async (facilityId) => {
    return await fetchApi(`/facilities/${facilityId}/billing_status`, "GET")
  },

  fetchBills: async (params) => {
    const queryParams = new URLSearchParams({
      page: params.page || 1,
      per_page: params.per_page || 10,
    }).toString();
    return await fetchApi(`/billing?${queryParams}`, "GET");
  },

  /**
   * Get closed bills
   * @returns
   */
  fetchClosedBills: async (params) => {
    const queryParams = new URLSearchParams({
      page: params.page || 1,
      per_page: params.per_page || 10,
    }).toString();
    return await fetchApi(`/closed/bills?${queryParams}`, "GET");
  },

  /**
   *  Get bill
   * @param {*} billId
   * @returns
   */
  getBill: async (billId) => {
    return await fetchApi("/billing/" + billId, "GET");
  },

  // get bill by visit
  getBillByVisit: async (visitId) => {
    return await fetchApi("/billing/visit/" + visitId, "GET");
  },

  /**
   * Get bill items
   * @param {*} billId
   * @returns
   */
  getPaymentMethods: async () => {
    return await fetchApi('/payment_method', "GET");
  },

  /**
   * sAVE PAYMENT TRANSACTION
   * @param {*} billId
   * @returns
   */
  savePaymentTransaction: async (payload) => {
    return await fetchApi("/billing", "POST", payload);
  },

  /**
   * Get Pharmacy prescriptions
   * @returns
   */
  listPatientPrescriptions: async () => {
    return await fetchApi("/pharmacy", "GET");
  },

  /**
   * Get visits whose prescriptions are fully issued
   * Powers the "Issued Prescriptions" second table in the pharmacy queue
   */
  listIssuedPrescriptions: async () => {
    return await fetchApi("/pharmacy/issued", "GET");
  },

  /**
   * Fetch Patient Pharmacy prescription Details
   * @returns
   */
  fetchPatientPrescriptionDetails: async (id) => {
    return await fetchApi("/pharmacy/" + id, "GET");
  },

  /**
   * Fetch Patient Pharmacy prescription Details
   * @returns
   */
  confirmPatientPrescriptionAvailability: async (id) => {
    return await fetchApi("/prescription/confirm/availability/" + id, "GET");
  },

  /**
   * Update Patient Pharmacy prescription
   * @returns
   */
  updatePharmacyPrescription: async (payload) => {
    return await fetchApi("/prescription/update", "POST", payload);
  },

  /**
   * Approve patient visit prescription items
   * @param {*} params
   * @returns
   */
  updatePharmacyPrescriptionsStatus: async (payload) => {
    return await fetchApi("/prescription/update/status", "POST", payload);
  },

  /**
   * Issue patient visit prescriptions
   * @param {*} params
   * @returns
   */
  issuePharmacyPrescription: async (payload) => {
    return await fetchApi("/pharmacy", "POST", payload);
  },

  /**
   * Search Pharmacy prescriptions
   * @returns
   */
  searchPatientPrescriptions: async (payload) => {
    return await fetchApi("/prescription/search", "POST", payload);
  },

  fetchExternalPrescription: async (visitId) => {
    return await fetchApi(`/external_prescriptions?visit_id=${visitId}`, "GET");
  },

  saveExternalPrescription: async (payload) => {
    return await fetchApi("/external_prescriptions", "POST", payload);
  },

  addPrescriptionsToBill: async (payload) => {
    return await fetchApi('/pharmacy/add_to_bill', 'POST', payload);
  },

  getOtcExternalPrescriptions: async (params = {}) => {
    const query = new URLSearchParams({
      page:     params.page || 1,
      per_page: params.per_page || 10,
    }).toString()
    return await fetchApi(`/otc_external_prescriptions?${query}`, 'GET')
  },

  saveOtcExternalPrescription: async (payload) => {
    return await fetchApi('/otc_external_prescriptions', 'POST', payload)
  },

  verifyPharmacyCoverage: async (payload) => {
    return await fetchApi('/pharmacy/verify_pharmacy_coverage', 'POST', payload);
  },

  /**
   *
   * @param {*} data
   * @returns
   */

  fetchFacility: async (facility_id) => {
    return await fetchApi(`/facilities/${facility_id}`, "GET")
  },

  fetchFacilityDoctors: async (params = {}) => {
    return await getWithParams("/doctor", params);
  },

  /**
   * search patients
   * @param {*} payload
   * @returns
   */
  searchPatient: async (payload) => {
    return await fetchApi("/patients/search", "POST", payload);
  },
  /**
   * Delete prescription item
   * @returns {Promise<*>}
   * @param id
   */
  deletePrescription: async (id) => {
    return await fetchApi("/pharmacy/" + id, "DELETE");
  },

  /**
   * List radiology tests
   * @returns {Promise<*>}
   */
  listPatientRadiologyTests: async (params) => {
    const queryParams = new URLSearchParams({
      page: params.page || 1,
      per_page: params.per_page || 10,
    }).toString();

    return await fetchApi(`/radiology?${queryParams}`, "GET");
  },

  getPatientVisitRadiologyTests: async (visitId) => {
    return await fetchApi("/radiology/" + visitId, "GET");
  },

  /**
   * Reject radiology test
   * @param {*} payload
   * @returns
   */
  rejectRadiologyTest: async (payload) => {
    return await fetchApi("/reject/radiology/test", "POST", payload);
  },

  verifyRadiologyCoverage: async (params) => {
    const queryParams = new URLSearchParams({
      patient_id: params.patient_id,
      visit_id: params.visit_id,
      bill_id: params.bill_id,
    }).toString();

    return await fetchApi(`/radiology/verify_radiology_coverage?${queryParams}`, "GET");
  },

  /**
   * Submit radiology test result
   * @param payload
   * @returns {Promise<*>}
   */
  submitRadiologyTestResult: async (payload) => {
    return await fetchApi("/conduct/radiology/test", "POST", payload);
  },

  /**
   * Review radiology test results
    * @param payload
   * @returns {Promise<*>}
   */
  reviewRadiologyResults: async (payload) => {
    return await fetchApi("/review/radiology/test", "POST", payload);
  },

  addExternalRadiologyTest: async (payload) => {
    return await fetchApi("/radiology/external_test", "POST", payload);
  },

  downloadInventoryItemsTemplate: async () => {
    // return await fetchApi(`/inventory_items/template`, "GET");

    const response = await fetch(`${BASE_URL}/inventory_items/template`, {
      method: 'GET',
      headers: {
        'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        // Add authentication headers if required, e.g.:
        // 'Authorization': `Bearer ${localStorage.getItem('token')}`,
      },
    });
    return response;
  },

  downloadInventoryStocksTemplate: async () => {
    const response = await fetch(`${BASE_URL}/inventory_stock/template`, {
      method: 'GET',
      headers: {
        'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      }
    })
    return response;
  },

  /**
   * Upload inventory items
   * @param payload
   * @returns {Promise<*>}
   */
  uploadInventoryItems: async (payload) => {
    console.log('payload', payload);
    return await fetchApiForFiles("/inventory_items/upload", "POST", payload);
  },

  uploadInventoryStocks: async (payload) => {
    console.log('stock', payload)
    return await fetchApiForFiles("/inventory_stock/upload", "POST", payload)
  },

  /**
   * Update an inventory stock item (batch).
   * The backend also syncs the parent InventoryStoreItem totals via quantity delta.
   * @param {string} id - stock item UUID
   * @param {object} payload - { batch_no, expiry_date, actual_quantity, cost_price, selling_price }
   */
  updateInventoryStockItem: async (id, payload) => {
    return await fetchApi("/inventory_stock/" + id, "PUT", payload);
  },

  /**
   * Delete an inventory stock item (batch).
   * The backend deducts its quantities from the parent InventoryStoreItem before deleting.
   * @param {string} id - stock item UUID
   */
  deleteInventoryStockItem: async (id) => {
    return await fetchApi("/inventory_stock/" + id, "DELETE");
  },


  // FFS Management APIs

  createCompany: async (payload) => {
    return await fetchApi("/companies", "POST", payload)
  },

  fetchFFSCoveredPatients: async (payload) => {
    const params = new URLSearchParams({
      page: payload.page || 1,
      per_page: payload.per_page || 10
    });

    // Flatten the filter
    if (payload.filter && typeof payload.filter === 'object') {
      Object.entries(payload.filter).forEach(([key, value]) => {
        params.append(`filter[${key}]`, value)
      })
    }

    const queryParams = params.toString();
    return await fetchApi(`/ffs_covered_patients?${queryParams}`, 'GET')
  },

  updateFFSCoverage: async (patient_id, payload) => {
    return await fetchApi(`/ffs_covered_patients/${patient_id}`, 'PUT', payload)
  },

  downloadCompanyBeneficiariesTemplate: async () => {
    const response = await fetch(`${BASE_URL}/companies/beneficiaries_template`, {
      method: 'GET',
      headers: {
        'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      }
    })

    return response;
  },

  uploadCompanyBeneficiaries: async (payload) => {
    return await fetchApiForFiles("/companies/upload_beneficiaries", "POST", payload)
  },

  addCompanyBeneficiaries: async (companyId, payload) => {
    return await fetchApi(`/companies/${companyId}/beneficiaries`, 'POST', payload)
  },

  fetchFFSCompaniesList: async (params) => {
    const queryParams = new URLSearchParams({
      page: params.page || 1,
      per_page: params.per_page || 10
    }).toString();

    return await fetchApi(`/companies?${queryParams}`, 'GET')
  },

  fetchCompanyWithBeneficiaries: async (id, params) => {
    const queryParams = new URLSearchParams({
      page: params.page || 1,
      per_page: params.per_page || 10
    }).toString();

    return await fetchApi(`/companies/${id}?${queryParams}`, 'GET')
  },

  updateBeneficiary: async (id, payload) => {
    const body = {
      beneficiary: payload
    }

    return await fetchApi(`/beneficiaries/${id}`, 'PUT', body)
  },

  deleteBeneficiary: async (id) => {
    return await fetchApi(`/beneficiaries/${id}`)
  },

  updateCompany: async (id, payload) => {
    const body = {
      company: payload
    }

    return await fetchApi(`/companies/${id}`, 'PUT', body)
  },

  deleteCompany: async (id) => {
    return await fetchApi(`/companies/${id}`, 'DELETE')
  },

  searchCompanies: async () => {
    return await fetchApi('/companies/search_companies')
  },

  searchBeneficiary: async (payload) => {
    return await fetchApi('/beneficiaries/search_beneficiary', 'POST', payload)
  },

  /**
   * Fetch facility system bills
   * @param params
   * @returns {Promise<*>}
   */
  fetchFacilitySystemBills: async (params) => {
    const queryParams = new URLSearchParams({
      page: params.page || 1,
      per_page: params.per_page || 10,
    }).toString();
    return await fetchApi(`/facility_bill?${queryParams}`, "GET");
  },

  /**
   * Initiate system bill payment via M-PESA stk push
   * @param payload
   * @returns {Promise<*>}
   */
  initiatePaymentStkPush: async (payload) => {
    return await fetchApi("/initiate/stk", "POST", payload);
  },

  /**
   * Fetch bill due for payment
   * @returns {Promise<*>}
   */
  fetchBillDue: async () => {
    return await get('/fetch/bill/due');
  },
  /**
   * Confirm bill payment status
   * @returns {Promise<*>}
   */
  confirmBillPaymentStatus: async (id) => {
    return await fetchApi(`/facilities/${id}/confirm_bill_payment`, 'POST');
  },

  /**
   * Fetch facility scheme list
   * @param params
   * @returns {Promise<*>}
   */
  fetchFacilitySchemeList: async (params) => {
    const queryParams = new URLSearchParams({
      page: params.page || 1,
      per_page: params.per_page || 10,
    }).toString();
    return await fetchApi(`/scheme?${queryParams}`, "GET");
  },

  getAllInsurances: async () => {
    return await fetchApi("/insurance", "GET")
  },
  /**
   * Search insurance companies
   * @param params
   * @returns {Promise<*>}
   */
  searchInsurance: async (params) => {
    return await fetchApi("/insurance/search", "POST", params);
  },

  /**
   * Create a new insurance scheme
   * @param data
   * @returns {Promise<*>}
   */
  createScheme: async (data) => {
    return await fetchApi("/scheme", "POST", data);
  },

  updateScheme: async (schemeId, data) => {
    return await fetchApi(`/scheme/${schemeId}`, "PUT", data);
  },

  /**
   * Soft delete a scheme
   * @param schemeId
   * @returns {Promise<*>}
   */
  deleteScheme: async (schemeId) => {
    return await fetchApi(`/scheme/${schemeId}`, "DELETE");
  },

  /**
   * Fetch insurance schemes by insurance ID
   * @param insuranceId
   * @returns {Promise<*>}
   */
  fetchInsuranceSchemes: async (insuranceId) => {
    return await fetchApi(`/insurance/${insuranceId}/scheme`, "GET");
  },

  /**
   * GEt facility cash scheme
   * @returns {Promise<*>}
   */
  fetchFacilityCashScheme: async () => {
    return await fetchApi("/facility/cash/schemes", "GET");
  },

  fetchStatuses: async () => {
    return await fetchApi("/statuses", "GET");
  },

  /**
   * Create an insurance claim for MTIBA
   * @param claimData
   * @returns {Promise<*>}
   */
  createInsuranceClaim: async (claimData) => {
    return await fetchApi("/insurance_claims", "POST", claimData);
  },

  /**
   * Fetch MTIBA beneficiary data without creating reservation
   * @param {Object} payload - Contains visit_code
   * @returns {Promise<*>}
   */
  fetchMtibaBeneficiary: async (payload) => {
    return await fetchApi("/mtiba/fetch_beneficiary", "POST", payload);
  },

  /**
   * Create MTIBA reservation after beneficiary verification
   * @param {Object} payload - Contains visit_code
   * @returns {Promise<*>}
   */
  createMtibaReservation: async (payload) => {
    return await fetchApi("/mtiba/create_reservation", "POST", payload);
  },

  /**
   * Update MTIBA reservation visit_id
   * @param {string} reservationId - The reservation ID
   * @param {string} visitId - The visit ID to associate
   * @returns {Promise<*>}
   */
  updateMtibaReservationVisitId: async (reservationId, visitId) => {
    return await fetchApi(`/mtiba/reservations/${reservationId}/update_visit_id`, "PUT", { visit_id: visitId });
  },

  /**
   * Delete MTIBA reservation
   * @param {string} reservationId - The reservation ID to delete
   * @returns {Promise<*>}
   */
  deleteMtibaReservation: async (reservationId) => {
    return await fetchApi(`/mtiba/reservations/${reservationId}`, "DELETE");
  },

  /**
   * Fetch MTIBA beneficiary data from external API
   * @param {Object} payload - Contains visit_code
   * @returns {Promise<*>}
   */
  fetchMtibaBeneficiaryData: async (payload) => {
    return await fetchApi("/mtiba/fetch_beneficiary", "POST", payload);
  },

  /**
   * Fetch MTIBA reservation by visit ID
   * @param visitId
   * @returns {Promise<*>}
   */
   fetchMtibaReservationByVisitId: async (visitId) => {
     return await fetchApi(`/mtiba/reservations/by_visit/${visitId}`, "GET");
   },

   /**
    * Check SHA eligibility for a patient
    * @param {Object} payload - Contains id_number, id_type
    * @returns {Promise<*>}
    */
    checkShaEligibility: async (payload) => {
      return await fetchApi("/sha/check_eligibility", "POST", payload);
    },

    sendShaOtp: async (payload) => {
      return await fetchApi("/sha/send_otp", "POST", payload);
    },

    getShaPatientInterventions: async (patientId, subBenefitCode = 'SHA-12') => {
      return await fetchApi(`/sha/patient_interventions?patient_id=${encodeURIComponent(patientId)}&sub_benefit_code=${encodeURIComponent(subBenefitCode)}`, "GET");
    },

    getShaPatientSubBenefits: async (patientId) => {
      return await fetchApi(`/sha/patient_sub_benefits?patient_id=${encodeURIComponent(patientId)}`, "GET");
    },

    /**
     * Start SHA visit consent with OTP
     * @param {Object} payload - Contains otp, cr_number, service_type, initial_interventions
     * @returns {Promise<*>}
     */
     startShaVisitConsent: async (payload) => {
      return await fetchApi("/sha/start_visit_consent", "POST", payload);
    },

    /**
     * Create SHA authorization using OTP (alternative flow)
     * @param {Object} payload - Contains otp, patient_id, service_type, interventions
     * @returns {Promise<*>}
     */
    startShaAuthorization: async (payload) => {
      return await fetchApi("/sha/authorize", "POST", payload);
    },

   /**
    * Add SHA intervention to claim
    * @param {Object} payload - Contains bill_item_id, intervention_code, service_type
    * @returns {Promise<*>}
    */
   addShaIntervention: async (payload) => {
     return await fetchApi("/sha/interventions/add", "POST", payload);
   },

   /**
    * Retire SHA intervention
    * @param {string} bill_item_id
    * @returns {Promise<*>}
    */
   retireShaIntervention: async (bill_item_id) => {
     return await fetchApi(`/sha/interventions/retire`, "POST", { bill_item_id });
   },

   /**
    * Switch SHA intervention to different code
    * @param {Object} payload - Contains bill_item_id, new_intervention_code
    * @returns {Promise<*>}
    */
    switchShaIntervention: async (payload) => {
      return await fetchApi("/sha/interventions/switch", "POST", payload);
    },

    /**
     * Resubmit SHA intervention for a bill item (retry submission)
     * @param {string} bill_item_id
     * @returns {Promise<*>}
     */
    resubmitShaIntervention: async (bill_item_id) => {
      return await fetchApi("/sha/interventions/resubmit", "POST", { bill_item_id });
    },

    /**
     * Submit SHA preauthorization
    * @param {string} preauth_type - surgical, imaging, oncology, renal
    * @param {Array} bill_item_ids - Array of bill item IDs
    * @returns {Promise<*>}
    */
   submitShaPreauth: async (preauth_type, bill_item_ids) => {
     return await fetchApi(`/sha/preauthorizations/${preauth_type}`, "POST", { bill_item_ids });
   },

   /**
    * Get SHA claim preview
    * @param {string} visit_number
    * @returns {Promise<*>}
    */
   previewShaClaim: async (visit_number) => {
     return await fetchApi(`/sha/claims/${visit_number}/preview`, "GET");
   },

    /**
     * Dispatch SHA claim
     * @param {string} visit_number
     * @param {object} payload
     * @param {string} payload.consent_token
     * @param {string} [payload.invoice_number]
     * @param {string} [payload.discharge_reason]
     * @param {string} [payload.otp]
     * @param {string} [payload.notes]
     * @returns {Promise<*>}
     */
    dispatchShaClaim: async (visit_number, payload = {}) => {
      return await fetchApi(`/sha/claims/${visit_number}/dispatch`, "POST", payload);
    },

   /**
    * Close SHA claim
    * @param {string} visit_number
    * @param {string} reason
    * @returns {Promise<*>}
    */
   closeShaClaim: async (visit_number, reason) => {
     return await fetchApi(`/sha/claims/${visit_number}/close`, "POST", { reason });
   },

   /**
    * Fetch insurance claims
    * @param params
    * @returns {Promise<*>}
    */
  getInsuranceClaims: async (params) => {
    const queryParams = new URLSearchParams({
      page: params.page || 1,
      per_page: params.per_page || 10,
    }).toString();

    return await fetchApi(`/insurance_claims?${queryParams}`, "GET");
  },

  /**
   * Submit claim to MTIBA
   * @param claimId
   * @returns {Promise<*>}
   */
  submitClaimToMtiba: async (claimId) => {
    return await fetchApi(`/insurance_claims/${claimId}/submit_to_mtiba`, "POST");
  },

    // ── Quality Control ────────────────────────────────────────
  listLabAnalytes: async (params = {}) => {
    const query = params.discipline ? `?discipline=${params.discipline}` : ''
    return await fetchApi(`/lab_analytes${query}`, 'GET')
  },

  listQcLots: async (params = {}) => {
    const queryParams = new URLSearchParams(params).toString()
    return await fetchApi(`/qc_lots?${queryParams}`, 'GET')
  },

  createQcLot: async (data) => {
    return await fetchApi('/qc_lots', 'POST', { qc_lot: data })
  },

  updateQcLot: async (id, data) => {
    return await fetchApi(`/qc_lots/${id}`, 'PATCH', { qc_lot: data })
  },

  toggleQcLot: async (id) => {
    return await fetchApi(`/qc_lots/${id}/toggle_active`, 'PATCH')
  },

  listQcResults: async (params = {}) => {
    const queryParams = new URLSearchParams(params).toString()
    return await fetchApi(`/qc_results?${queryParams}`, 'GET')
  },

  createQcResult: async (data) => {
    return await fetchApi('/qc_results', 'POST', { qc_result: data })
  },

  listLabEquipment: async (params = {}) => {
    const q = new URLSearchParams(params).toString()
    return fetchApi(`/lab_equipment?${q}`, 'GET')
  },
  
  createLabEquipment: async (data) => fetchApi('/lab_equipment', 'POST', { lab_equipment: data }),

  updateLabEquipment: async (id, data) => fetchApi(`/lab_equipment/${id}`, 'PATCH', { lab_equipment: data }),

  listEquipmentLogs: async (equipmentId) =>
    fetchApi(`/lab_equipment/${equipmentId}/lab_equipment_logs`, 'GET'),

  createEquipmentLog: async (equipmentId, data) =>
    fetchApi(`/lab_equipment/${equipmentId}/lab_equipment_logs`, 'POST', { lab_equipment_log: data }),

  listLabPanels: async () => fetchApi('/lab_panels', 'GET'),

  createLabPanel: async (data) => fetchApi('/lab_panels', 'POST', { lab_panel: data }),

  updateLabPanel: async (id, data) => fetchApi(`/lab_panels/${id}`, 'PATCH', { lab_panel: data }),

  deleteLabPanel: async (id) => fetchApi(`/lab_panels/${id}`, 'DELETE'),

  addPanelItem: async (panelId, facilityServiceId) =>
    fetchApi(`/lab_panels/${panelId}/lab_panel_items`, 'POST', { lab_panel_item: { facility_service_id: facilityServiceId } }),

  removePanelItem: async (panelId, itemId) =>
    fetchApi(`/lab_panels/${panelId}/lab_panel_items/${itemId}`, 'DELETE'),

  listRadiologyTemplates: async () => fetchApi('/radiology_templates', 'GET'),

  createRadiologyTemplate: async (data) => fetchApi('/radiology_templates', 'POST', { radiology_template: data }),

  updateRadiologyTemplate: async (id, data) => fetchApi(`/radiology_templates/${id}`, 'PATCH', { radiology_template: data }),

  deleteRadiologyTemplate: async (id) => fetchApi(`/radiology_templates/${id}`, 'DELETE'),

  addTemplateSection: async (templateId, data) =>
    fetchApi(`/radiology_templates/${templateId}/radiology_template_sections`, 'POST', { radiology_template_section: data }),

  updateTemplateSection: async (templateId, sectionId, data) =>
    fetchApi(`/radiology_templates/${templateId}/radiology_template_sections/${sectionId}`, 'PATCH', { radiology_template_section: data }),

  removeTemplateSection: async (templateId, sectionId) =>
    fetchApi(`/radiology_templates/${templateId}/radiology_template_sections/${sectionId}`, 'DELETE'),

  getMoh717Report: async (params) => {
    const queryParams = new URLSearchParams(params).toString()
    return await fetchApi(`/consultation_reports/moh_717?${queryParams}`, "GET")
  }
};

export default apiClient;